import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
// Note: Consumers should import their own date-picker.css for custom styling
import { Box, Button, VStack, } from "@chakra-ui/react";
import { FaLongArrowAltRight } from "react-icons/fa";
const DateSelectionCalendar = ({ selectedDate, onDateChange, onContinue, hideBorder = false, bg, pubColors, }) => {
    const [isCalendarOpen, setIsCalendarOpen] = useState(true);
    // Default colors matching the original hardcoded values
    const defaultColors = {
        primary: "#8155FE",
        secondary: "#A383FF",
        text: "#FFFFFF",
        bg: "#1E1E1E",
        card_border: "#3A3D43",
    };
    // Use pubColors if provided, otherwise use defaults
    const colors = pubColors || defaultColors;
    // Get today's date at midnight to compare dates properly
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Filter function to allow only today and future dates
    const isDateAllowed = (date) => {
        const dateToCheck = new Date(date);
        dateToCheck.setHours(0, 0, 0, 0);
        return dateToCheck >= today;
    };
    const formatSelectedDate = (date) => {
        if (!date)
            return "Select a date";
        const options = {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
        };
        return date.toLocaleDateString("en-US", options);
    };
    const formatDateShort = (date) => {
        if (!date)
            return "";
        const options = {
            month: "short",
            day: "numeric",
        };
        return date.toLocaleDateString("en-US", options);
    };
    return (_jsxs(VStack, { w: "100%", spacing: 4, align: "stretch", children: [_jsx(Box, { w: "100%", bg: bg || colors.bg, borderRadius: "16px", p: 6, border: hideBorder ? "none" : `1px solid ${colors.card_border}`, overflow: "hidden", children: _jsx(Box, { w: "100%", sx: {
                        ".react-datepicker": {
                            background: "transparent !important",
                            border: "none !important",
                            fontFamily: "inherit",
                            width: "100% !important",
                        },
                        ".react-datepicker__month-container": {
                            width: "100% !important",
                        },
                        ".react-datepicker__header": {
                            background: "transparent !important",
                            borderBottom: `1px solid ${colors.card_border} !important`,
                            borderRadius: "0 !important",
                            padding: "0 0 16px 0 !important",
                            marginBottom: "16px !important",
                            position: "relative !important",
                        },
                        ".react-datepicker__current-month": {
                            color: `${colors.text} !important`,
                            fontSize: "16px !important",
                            fontWeight: "400 !important",
                            marginBottom: "20px !important",
                            marginTop: "0 !important",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            paddingTop: "0 !important",
                        },
                        ".react-datepicker__day-names": {
                            display: "flex",
                            justifyContent: "space-between",
                            padding: "0 !important",
                            marginBottom: "8px !important",
                            marginTop: "0 !important",
                        },
                        ".react-datepicker__day-name": {
                            color: `${colors.primary} !important`,
                            fontSize: "12px !important",
                            fontWeight: "700 !important",
                            width: "calc((100% - 24px) / 7) !important",
                            maxWidth: "40px !important",
                            lineHeight: "24px !important",
                            margin: "0 !important",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                        },
                        ".react-datepicker__month": {
                            margin: "0 !important",
                            padding: "0 !important",
                            width: "100% !important",
                        },
                        ".react-datepicker__week": {
                            display: "flex",
                            justifyContent: "space-between",
                            margin: "8px 0 !important",
                            width: "100% !important",
                            gap: "4px !important",
                        },
                        ".react-datepicker__day": {
                            color: `${colors.text} !important`,
                            fontSize: "15px !important",
                            fontWeight: "500 !important",
                            width: "calc((100% - 24px) / 7) !important",
                            maxWidth: "40px !important",
                            height: "40px !important",
                            lineHeight: "40px !important",
                            margin: "0 !important",
                            borderRadius: "12px !important",
                            transition: "all 0.2s ease !important",
                            display: "flex !important",
                            alignItems: "center !important",
                            justifyContent: "center !important",
                            "&:hover": {
                                background: `${colors.primary}4D !important`,
                                color: `white !important`,
                                transform: "scale(1.05)",
                            },
                        },
                        ".react-datepicker__day--disabled": {
                            color: `${colors.text} !important`,
                            cursor: "not-allowed !important",
                            opacity: "0.4 !important",
                            "&:hover": {
                                background: "transparent !important",
                                transform: "none !important",
                                color: `${colors.text} !important`,
                            },
                        },
                        ".react-datepicker__day--selected": {
                            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primary} 100%) !important`,
                            color: `white !important`,
                            fontWeight: "700 !important",
                            boxShadow: `0 4px 12px ${colors.primary} !important`,
                            transform: "scale(1)",
                            "&:hover": {
                                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primary} 100%) !important`,
                                transform: "scale(1.05)",
                            },
                        },
                        ".react-datepicker__day--keyboard-selected": {
                            background: `${colors.primary}40 !important`,
                            color: `${colors.text} !important`,
                        },
                        ".react-datepicker__day--today": {
                            background: `${colors.primary}15 !important`,
                            color: `${colors.text} !important`,
                            fontWeight: "700 !important",
                            border: `2px solid ${colors.primary} !important`,
                            "&:hover": {
                                background: `${colors.primary}25 !important`,
                                borderColor: `${colors.secondary} !important`,
                            },
                        },
                        ".react-datepicker__day--outside-month": {
                            color: `${colors.text} !important`,
                            opacity: "0.8",
                        },
                        ".react-datepicker__navigation": {
                            top: "-4px !important",
                            width: "36px !important",
                            height: "36px !important",
                            borderRadius: "10px !important",
                            background: `${colors.primary}10 !important`,
                            border: `1px solid ${colors.primary}20 !important`,
                            transition: "all 0.2s ease !important",
                            "&:hover": {
                                background: `${colors.primary}20 !important`,
                                borderColor: `${colors.primary} !important`,
                            },
                        },
                        ".react-datepicker__navigation--previous": {
                            left: "8px !important",
                        },
                        ".react-datepicker__navigation--next": {
                            right: "8px !important",
                        },
                        ".react-datepicker__navigation-icon": {
                            top: "50% !important",
                            left: "50% !important",
                            marginTop: "-6px !important",
                            marginLeft: "-6px !important",
                        },
                        ".react-datepicker__navigation-icon::before": {
                            borderColor: `${colors.primary} !important`,
                            borderWidth: "2.5px 2.5px 0 0 !important",
                            width: "8px !important",
                            height: "8px !important",
                            right: "6px !important",
                            left: "-14px !important",
                            top: "-4px !important",
                        },
                        ".react-datepicker__navigation--previous .react-datepicker__navigation-icon::before": {
                            transform: "rotate(-135deg) !important",
                            marginLeft: "-2px !important",
                        },
                        ".react-datepicker__navigation--next .react-datepicker__navigation-icon::before": {
                            transform: "rotate(45deg) !important",
                            marginLeft: "-6px !important",
                        },
                        ".react-datepicker__navigation:hover .react-datepicker__navigation-icon::before": {
                            borderColor: `${colors.secondary} !important`,
                        },
                    }, children: _jsx(DatePicker, { selected: selectedDate, onChange: (date) => {
                            onDateChange(date);
                        }, inline: true, minDate: today, filterDate: isDateAllowed, dateFormat: "yyyy-MM-dd", calendarClassName: "custom-calendar", showPopperArrow: false }) }) }), selectedDate && onContinue && (_jsxs(Button, { w: "100%", bg: colors.primary, color: "white", fontWeight: 400, fontSize: "md", py: 6, borderRadius: "10px", mt: 4, _hover: {
                    bg: colors.secondary,
                    transform: "translateY(-2px)",
                    boxShadow: `0 4px 12px ${colors.primary}`,
                }, _active: {
                    bg: colors.primary,
                    transform: "translateY(0)",
                }, transition: "all 0.2s", rightIcon: _jsx(FaLongArrowAltRight, { size: 20 }), onClick: onContinue, children: ["Continue booking for \u00A0", _jsx("strong", { children: formatDateShort(selectedDate) })] }))] }));
};
export default DateSelectionCalendar;
